import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { type AnyZodObject } from "zod";
import { useCustomFormContext } from "@/components/site-builder/modules/CustomFormModule/context/customFormContext";
import { useCustomFormDefaultValues } from "@/components/site-builder/modules/CustomFormModule/hooks/useCustomFormDefaultValues";
import { useCustomFormValidationSchema } from "@/components/site-builder/modules/CustomFormModule/hooks/useCustomFormValidationSchema";
import { useGoogleReCaptcha } from "@/components/site-builder/modules/CustomFormModule/hooks/useGoogleReCaptcha";
import { generateFieldName } from "@/components/site-builder/modules/CustomFormModule/utils/generateFieldName";
import { getFormFields } from "@/components/site-builder/modules/CustomFormModule/utils/getFormFields";
import { transformConsentValues } from "@/components/site-builder/modules/CustomFormModule/utils/transformConsentValues";
import { transformCustomFormValues } from "@/components/site-builder/modules/CustomFormModule/utils/transformCustomFormValues";
import { FormBuilderConsentFieldNames } from "@/constants/form-builder";
import { useClientConfig } from "@/context/clientConfig";
import { useTranslation } from "@/i18n/client";
import {
  checkThankYouPageExists,
  submitFormResponse,
} from "@/lib/client/form-builder";
import { isAbsoluteURL } from "@/utils/url/isAbsoluteURL";
import type { FormBuilderSubmitFormData } from "@/types/form-builder";

export interface UseCustomFormWithValidationProps {
  externalSchemas: Array<AnyZodObject | null>;
}

export function useCustomFormWithValidation(
  props: UseCustomFormWithValidationProps,
) {
  const { externalSchemas } = props;

  const { t } = useTranslation("form-elements");
  const {
    formBody: { formId },
    formEntity,
    isSubmitting,
    setIsSubmitting,
  } = useCustomFormContext();
  const router = useRouter();
  const { siteConfig } = useClientConfig();
  const defaultValues = useCustomFormDefaultValues({
    formEntity,
  });
  const validationSchema = useCustomFormValidationSchema({
    formEntity,
    externalSchemas,
  });
  const getReCaptchaToken = useGoogleReCaptcha();
  const [isShownThankYouFallback, setIsShownThankYouFallback] = useState(false);

  const thankYouPage = formEntity.data?.thankYouPage;
  const hasThankYouPage = !!thankYouPage;

  const form = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues,
  });

  const onSuccessSubmit = async () => {
    try {
      if (thankYouPage) {
        if (isAbsoluteURL(thankYouPage)) {
          router.push(thankYouPage);
          return;
        }

        const res = await checkThankYouPageExists({
          thankYouPage,
          siteConfig,
        });
        if (res.ok) {
          router.push(thankYouPage);
        } else {
          setIsShownThankYouFallback(true);
        }
      }
    } catch (error) {
      console.error(error);
      setIsShownThankYouFallback(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onErrorSubmit = (error: Error) => {
    if ("errors" in error) {
      const fieldErrors = error.errors as Record<string, string>;

      Object.keys(fieldErrors).forEach((errorFieldId) => {
        const fieldWithError = getFormFields(formEntity).find(
          (field) => field.id === errorFieldId,
        );

        if (fieldWithError) {
          const fieldWithErrorName = generateFieldName(fieldWithError);
          form.setError(
            fieldWithErrorName,
            {
              message: t(`validation.error.code.${fieldErrors[errorFieldId]}`),
            },
            {
              shouldFocus: true,
            },
          );
        }
      });
    }
    setIsSubmitting(false);
  };

  const {
    mutate,
    error,
    isSuccess,
    isError,
    reset: resetFormSubmission,
  } = useMutation({
    mutationFn: (formData: FormBuilderSubmitFormData) => {
      setIsSubmitting(true);

      return submitFormResponse({
        formId,
        formData,
        siteConfig,
        errorMessageFallback: t("form.error.fallback.message"),
      });
    },
    onSuccess: onSuccessSubmit,
    onError: onErrorSubmit,
  });

  const submitHandler: SubmitHandler<Record<string, unknown>> = async (
    values,
  ) => {
    const recaptchaToken = (await getReCaptchaToken?.()) || null;

    const {
      [FormBuilderConsentFieldNames.CombineMarketing]: combinedResponseSelected,
      [FormBuilderConsentFieldNames.ThirdParty]: thirdPartyResponseSelected,
      [FormBuilderConsentFieldNames.Marketing]: marketingResponseSelected,
      ...restValues
    } = values;

    return mutate({
      ...(recaptchaToken && { recaptchaToken }),
      items: transformCustomFormValues(restValues),
      consentResponse: transformConsentValues(
        combinedResponseSelected,
        marketingResponseSelected,
        thirdPartyResponseSelected,
      ),
    });
  };

  return {
    form,
    submitHandler,
    resetFormSubmission,
    isSubmitting,
    isShownThankYouMessage: hasThankYouPage
      ? isSuccess && isShownThankYouFallback
      : isSuccess,
    isError,
    error,
  };
}
